import storeConfigsService from './storeconfigs.service';

const WEBPRNT_PATH = '/StarWebPRNT/SendMessage';
const WEBPRNT_TIMEOUT = 15000; // 15 seconds
const SEPARATOR = '================================';

function escapeXml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Build Star WebPRNT XML from structured receipt data.
 *
 * receiptData shape:
 * {
 *   title: string,
 *   subtitle?: string,
 *   meta?: [{ label, value }],
 *   sections?: [{
 *     title?: string,
 *     rows?: [{ label, value, emphasize? }],
 *     items?: [{ text, price? }]
 *   }],
 *   footerNote?: string
 * }
 */
function buildReceiptXml(receiptData) {
  const NL = '&#x0a;';
  let xml = '';

  xml += '<initialization/>';

  // Header: store name centered + bold + large
  xml += '<alignment position="center"/>';
  xml += `<text emphasis="true" width="2" height="2" data="${escapeXml('LOS PORTALES')}${NL}"/>`;
  xml += `<text emphasis="false" width="1" height="1" data="${escapeXml('Panaderia y Pasteleria')}${NL}"/>`;

  // Title
  if (receiptData.title) {
    xml += `<text emphasis="true" data="${NL}${escapeXml(receiptData.title)}${NL}"/>`;
    xml += '<text emphasis="false"/>';
  }

  // Subtitle
  if (receiptData.subtitle) {
    xml += `<text data="${escapeXml(receiptData.subtitle)}${NL}"/>`;
  }

  // Meta fields
  xml += '<alignment position="left"/>';
  if (receiptData.meta && receiptData.meta.length > 0) {
    xml += `<text data="${SEPARATOR}${NL}"/>`;
    for (const { label, value } of receiptData.meta) {
      xml += `<text data="${escapeXml(label)}: ${escapeXml(String(value))}${NL}"/>`;
    }
  }

  // Sections
  for (const section of receiptData.sections || []) {
    xml += `<text data="${SEPARATOR}${NL}"/>`;
    if (section.title) {
      xml += `<text emphasis="true" data="${escapeXml(section.title)}${NL}"/>`;
      xml += '<text emphasis="false"/>';
    }
    for (const row of section.rows || []) {
      const val = String(row.value ?? '');
      if (row.emphasize) {
        xml += `<text emphasis="true" data="${escapeXml(row.label)}: ${escapeXml(val)}${NL}"/>`;
        xml += '<text emphasis="false"/>';
      } else {
        xml += `<text data="${escapeXml(row.label)}: ${escapeXml(val)}${NL}"/>`;
      }
    }
    for (const item of section.items || []) {
      let line = `  ${escapeXml(item.text)}`;
      if (item.price) {
        line += `  ${escapeXml(item.price)}`;
      }
      xml += `<text data="${line}${NL}"/>`;
    }
  }

  // Footer
  if (receiptData.footerNote) {
    xml += `<text data="${SEPARATOR}${NL}"/>`;
    xml += '<alignment position="center"/>';
    xml += `<text data="${escapeXml(receiptData.footerNote)}${NL}"/>`;
  }

  // Feed and partial cut
  xml += '<feed line="3"/>';
  xml += '<cutpaper feed="true" type="partial"/>';

  return xml;
}

/**
 * Send raw XML commands to the Star WebPRNT printer via HTTP POST.
 * Returns { success: boolean, error?: string }.
 */
async function sendToPrinter(printerUrl, xmlRequest) {
  const body = `<root>${xmlRequest}</root>`;

  try {
    const response = await fetch(printerUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/xml; charset=UTF-8' },
      body,
      signal: AbortSignal.timeout(WEBPRNT_TIMEOUT),
    });

    if (!response.ok) {
      return { success: false, error: `HTTP ${response.status}: ${response.statusText}` };
    }

    const xml = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(xml, 'application/xml');
    const root = doc.querySelector('root');
    const success = root?.getAttribute('success') === 'true';

    if (!success) {
      const status = root?.getAttribute('status') || '';
      return { success: false, error: `Error de impresora (status: ${status})` };
    }

    return { success: true };
  } catch (err) {
    if (err.name === 'TimeoutError' || err.name === 'AbortError') {
      return { success: false, error: 'Tiempo de espera agotado. Verifica que la impresora esté encendida.' };
    }
    return { success: false, error: `No se pudo conectar: ${err.message}` };
  }
}

class PrinterService {
  _configCache = null;
  _cacheTimestamp = 0;
  _cacheTtl = 5 * 60 * 1000; // 5 minutes

  /**
   * Get printer config for the current store.
   * Caches the result for 5 minutes to avoid repeated Firestore reads.
   */
  async getPrinterConfig() {
    const now = Date.now();
    if (this._configCache && (now - this._cacheTimestamp) < this._cacheTtl) {
      return this._configCache;
    }

    try {
      const storeConfig = await storeConfigsService.getStoreConfig();
      this._configCache = storeConfig.printerConfig || null;
      this._cacheTimestamp = now;
      return this._configCache;
    } catch {
      return null;
    }
  }

  clearCache() {
    this._configCache = null;
    this._cacheTimestamp = 0;
  }

  /**
   * Build the WebPRNT endpoint URL from printer config.
   */
  getPrinterUrl(config) {
    if (!config || !config.ip) return null;
    const protocol = config.protocol || 'http';
    return `${protocol}://${config.ip}${WEBPRNT_PATH}`;
  }

  /**
   * Check if direct printing is available for the current store.
   */
  async isDirectPrintAvailable() {
    const config = await this.getPrinterConfig();
    return config?.enabled && config?.ip;
  }

  /**
   * Print a receipt directly to the Star WebPRNT printer.
   * Returns { success: boolean, error?: string }.
   */
  async printReceipt(receiptData) {
    const config = await this.getPrinterConfig();

    if (!config?.enabled || !config?.ip) {
      return { success: false, error: 'Impresora no configurada' };
    }

    const url = this.getPrinterUrl(config);
    const xml = buildReceiptXml(receiptData);
    return sendToPrinter(url, xml);
  }

  /**
   * Send a test print to verify printer connectivity.
   */
  async testPrint(ip, protocol = 'http') {
    const url = `${protocol}://${ip}${WEBPRNT_PATH}`;
    const xml = buildReceiptXml({
      title: 'Prueba de Impresora',
      subtitle: 'Conexion exitosa',
      meta: [
        { label: 'Estado', value: 'OK' },
        { label: 'Fecha', value: new Date().toLocaleString('es-MX') },
      ],
      footerNote: 'Si ves esto, la impresora funciona correctamente.',
    });
    return sendToPrinter(url, xml);
  }

  /**
   * Scan a subnet for Star WebPRNT printers.
   * Calls onProgress(scanned, total) and onFound({ ip, status }) as results come in.
   * Returns array of all found devices.
   */
  async scanForPrinters(subnetBase, protocol = 'http', { onProgress, onFound, signal } = {}) {
    const BATCH_SIZE = 20;
    const PROBE_TIMEOUT = 2500;
    const results = [];

    for (let i = 1; i <= 254; i += BATCH_SIZE) {
      if (signal?.aborted) break;

      const batch = [];
      for (let j = i; j < Math.min(i + BATCH_SIZE, 255); j++) {
        batch.push(this._probeIp(`${subnetBase}.${j}`, protocol, PROBE_TIMEOUT));
      }

      const batchResults = await Promise.allSettled(batch);
      for (const result of batchResults) {
        if (result.status === 'fulfilled' && result.value) {
          results.push(result.value);
          onFound?.(result.value);
        }
      }
      onProgress?.(Math.min(i + BATCH_SIZE - 1, 254), 254);
    }

    return results;
  }

  /**
   * Probe a single IP for a Star WebPRNT printer.
   * Returns { ip, status: 'verified' | 'detected' } or null if nothing responds.
   */
  async _probeIp(ip, protocol, timeout) {
    // Step 1: Check if any HTTP server responds (no-cors avoids CORS blocking)
    try {
      await fetch(`${protocol}://${ip}/`, {
        mode: 'no-cors',
        signal: AbortSignal.timeout(timeout),
      });
    } catch {
      return null; // Nothing at this IP
    }

    // Step 2: Something responded — try the WebPRNT endpoint normally
    try {
      const url = `${protocol}://${ip}${WEBPRNT_PATH}`;
      const testXml = '<root><initialization/><text data="test&#x0a;"/></root>';
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/xml; charset=UTF-8' },
        body: testXml,
        signal: AbortSignal.timeout(timeout),
      });
      if (response.ok) {
        return { ip, status: 'verified' };
      }
      return { ip, status: 'detected' };
    } catch {
      // CORS error or similar — device exists but can't confirm it's a Star printer
      return { ip, status: 'detected' };
    }
  }
}

const printerService = new PrinterService();
export { buildReceiptXml, escapeXml };
export default printerService;
